import type { LayerFeature, SyncMode } from 'components/ImageViewer/types';

// Normalize image_index / spatial_index from API attributes or feature fields. 
export function normalizeImageIndex(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.trunc(parsed);
}

export function getFeatureImageIndex(
  feature: LayerFeature | null | undefined,
): number | null {
  return normalizeImageIndex(
    feature?.imageIndex ?? feature?.attributes?.image_index,
  );
}

export function getFeatureSpatialIndex(
  feature: LayerFeature | null | undefined,
): number | null {
  return normalizeImageIndex(
    feature?.spatialIndex ?? feature?.attributes?.spatial_index,
  );
}

export function getFeatureSyncValue(
  feature: LayerFeature | null | undefined,
  mode: SyncMode,
): number | null {
  return mode === 'spatial'
    ? getFeatureSpatialIndex(feature)
    : getFeatureImageIndex(feature);
}

export function hasSpatialFocusValue(
  feature: LayerFeature | null | undefined,
): boolean {
  return getFeatureSpatialIndex(feature) !== null;
}

export function buildPlaybackTimelineSyncValues(
  layers: ReadonlyArray<{ readonly features: readonly LayerFeature[] }>,
  mode: SyncMode,
): number[] {
  const availableSyncValues = layers.flatMap((layer) =>
    layer.features
      .map((feature) => getFeatureSyncValue(feature, mode))
      .filter((value): value is number => value !== null),
  );

  if (availableSyncValues.length === 0) {
    return [];
  }

  return Array.from(new Set(availableSyncValues)).sort((a, b) => a - b);
}

export function resolvePlaybackStartPosition(
  layers: ReadonlyArray<{ readonly features: readonly LayerFeature[] }>,
  preferredSyncValue: number | null,
  syncMode: SyncMode,
): number {
  if (preferredSyncValue === null) {
    return 0;
  }
  const timelineIndexes = buildPlaybackTimelineSyncValues(layers, syncMode);
  const timelineIndex = timelineIndexes.findIndex(
    (value) => value === preferredSyncValue,
  );
  return timelineIndex >= 0 ? timelineIndex : 0;
}

export const NO_SPATIAL_INDEX_MESSAGE = 'No spatial index on this image';
export const NO_IMAGE_INDEX_MESSAGE = 'No image index on this image';
export const SYNC_PARTIAL_NO_MATCH_MESSAGE =
  'No matching image in one or more synced viewers';
export const SYNC_SEQUENCE_START_MESSAGE = 'Reached the beginning of the sequence';
export const SYNC_SEQUENCE_END_MESSAGE = 'Reached the end of the sequence';

export function syncFocusUnavailableMessage(mode: SyncMode): string {
  return mode === 'spatial' ? NO_SPATIAL_INDEX_MESSAGE : NO_IMAGE_INDEX_MESSAGE;
}

export interface SyncedJumpViewerLike {
  readonly id: string;
  readonly sequentialIndex: number;
  readonly layerFeatures: readonly LayerFeature[];
}

export type SyncedJumpSourceResult = {
  readonly sourceViewerId: string | null;
  readonly sourceFeatureIndex: number;
  readonly targetSyncValue: number | null;
};

/**
 * Pick a sync-jump source viewer that can provide a resolvable sync value.
 * Prefers the initiating viewer when its target feature is valid; otherwise
 * falls back to another group member so one bad source does not blank all.
 */
export function resolveSyncedJumpSource(
  viewers: ReadonlyArray<SyncedJumpViewerLike>,
  viewerIds: readonly string[],
  targetIndex: number,
  syncMode: SyncMode,
  excludeViewerId: string | null,
  referenceValue?: number | null,
): SyncedJumpSourceResult {
  const byId = new Map(viewers.map((viewer) => [viewer.id, viewer]));

  const tryViewerAtIndex = (
    viewerId: string,
    featureIndex: number,
  ): SyncedJumpSourceResult | null => {
    const viewer = byId.get(viewerId);
    if (!viewer) return null;
    if (featureIndex < 0 || featureIndex >= viewer.layerFeatures.length) {
      return null;
    }
    const syncValue = getFeatureSyncValue(
      viewer.layerFeatures[featureIndex],
      syncMode,
    );
    if (syncValue === null) return null;
    return {
      sourceViewerId: viewerId,
      sourceFeatureIndex: featureIndex,
      targetSyncValue: syncValue,
    };
  };

  if (referenceValue !== null && referenceValue !== undefined) {
    const preferredId =
      excludeViewerId && byId.has(excludeViewerId)
        ? excludeViewerId
        : viewerIds.find((id) => byId.has(id)) ?? null;
    return {
      sourceViewerId: preferredId,
      sourceFeatureIndex: targetIndex,
      targetSyncValue: referenceValue,
    };
  }

  if (excludeViewerId) {
    const preferred = tryViewerAtIndex(excludeViewerId, targetIndex);
    if (preferred) return preferred;
  }

  for (const viewerId of viewerIds) {
    if (viewerId === excludeViewerId) continue;
    const candidate = tryViewerAtIndex(viewerId, targetIndex);
    if (candidate) return candidate;
  }

  for (const viewerId of viewerIds) {
    const viewer = byId.get(viewerId);
    if (!viewer) continue;
    const candidate = tryViewerAtIndex(viewerId, viewer.sequentialIndex);
    if (candidate) return candidate;
  }

  return {
    sourceViewerId: excludeViewerId,
    sourceFeatureIndex: targetIndex,
    targetSyncValue: null,
  };
}

export type SyncFocusModeChangeResult =
  | {
      readonly ok: true;
      readonly mode: SyncMode;
      readonly referenceValue: number;
      readonly focusViewerId: string | null;
    }
  | {
      readonly ok: false;
      readonly message: string;
    };

export function resolveSyncFocusModeChange(
  feature: LayerFeature | null | undefined,
  useSpatialFocus: boolean,
  viewerId: string,
): SyncFocusModeChangeResult {
  const mode: SyncMode = useSpatialFocus ? 'spatial' : 'image';
  const referenceValue = getFeatureSyncValue(feature, mode);
  if (referenceValue === null) {
    return { ok: false, message: syncFocusUnavailableMessage(mode) };
  }
  return {
    ok: true,
    mode,
    referenceValue,
    focusViewerId: useSpatialFocus ? viewerId : null,
  };
}

export type PlaybackSyncModeChangeResult =
  | {
      readonly ok: true;
      readonly mode: SyncMode;
      readonly nextIndex: number;
      readonly focusOwnerLayerKey: string | null;
    }
  | {
      readonly ok: false;
      readonly message: string;
    };

export function resolvePlaybackSyncModeChange(
  layers: ReadonlyArray<{ readonly features: readonly LayerFeature[] }>,
  feature: LayerFeature,
  layerKey: string,
  mode: SyncMode,
): PlaybackSyncModeChangeResult {
  const referenceValue = getFeatureSyncValue(feature, mode);
  if (referenceValue === null) {
    return { ok: false, message: syncFocusUnavailableMessage(mode) };
  }

  const timeline = buildPlaybackTimelineSyncValues(layers, mode);
  const nextIndex = timeline.findIndex((value) => value === referenceValue);
  if (nextIndex < 0) {
    return { ok: false, message: syncFocusUnavailableMessage(mode) };
  }

  return {
    ok: true,
    mode,
    nextIndex,
    focusOwnerLayerKey: mode === 'spatial' ? layerKey : null,
  };
}

